import React, {
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import type {
  Fare,
  FareSliceDeparture,
  FareSliceOutbound,
} from "@b2bportal/air-shopping-api";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type IFlightListData,
  type FlightFareSelectionFunction,
  type PlatformComponentProps,
  type Funnel,
} from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { FlightCard } from "@tenant/flights";
import clsx from "clsx";
import { useFlightSlot } from "../../../../slots";
import {
  FlightRawEventName,
  useEmitCoreFlightRawEventOnce,
} from "../../../../tracking";
import defaultStyles from "./FlightList.module.scss";

export interface FlightListComponentProps extends PlatformComponentProps {
  flightList?: IFlightListData<Fare | FareSliceOutbound | FareSliceDeparture>[];
  sliceIndex?: number;
  onFareSelect: FlightFareSelectionFunction;
  goToNextStep: () => void;
  className?: string;
  isAllCollapsed?: boolean;
  setIsAllCollapsed?: (collapsed: boolean) => void;
  funnel?: Funnel;
}

const config = {
  INITIAL_RESULT_SET_SIZE: 20,
  SHOW_MORE_NUM: 20,
  DESKTOP_OFFSET_SCROLL: 150,
  MOBILE_OFFSET_SCROLL: 150,
};

export const FlightList = ({
  flightList = [],
  sliceIndex = 0,
  onFareSelect,
  goToNextStep,
  className,
  children,
  isAllCollapsed,
  setIsAllCollapsed,
  funnel,
}: FlightListComponentProps) => {
  const styles = useFlightStyles(
    CoreFlightsComponent.FlightList,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, CoreFlightsComponent.FlightList);

  const FlightSlot = useFlightSlot();

  const [numberOfFlightsRendered, setNumFlightsToShow] = useState(
    config.INITIAL_RESULT_SET_SIZE
  );

  const [expandedFlightCard, setExpandedFlightCard] = useState<string>();

  const setFetchMoreData = useCallback(() => {
    return setTimeout(
      () => setNumFlightsToShow(numberOfFlightsRendered + config.SHOW_MORE_NUM),
      500
    );
  }, [numberOfFlightsRendered]);

  const { isMobile, isTablet } = useDeviceType();

  const scrollToView = (ref: React.RefObject<HTMLDivElement>) => {
    setTimeout(() => {
      if (ref?.current) {
        const OFFSET =
          isMobile && isTablet
            ? config.MOBILE_OFFSET_SCROLL
            : config.DESKTOP_OFFSET_SCROLL;
        const cardTop = ref.current?.getBoundingClientRect().top || 0;
        window.scrollBy({
          top: (cardTop as number) - OFFSET,
          behavior: "smooth",
        });
      }
    }, 200);
  };

  const refs = useRef<Array<React.RefObject<HTMLDivElement>>>([]);

  useEffect(() => {
    flightList.forEach((_, index) => {
      refs.current[index] = refs.current[index] ?? createRef();
    });
  }, [flightList]);

  useEffect(() => {
    if (isAllCollapsed === true) {
      setExpandedFlightCard("");
    }
  }, [isAllCollapsed]);

  useEmitCoreFlightRawEventOnce({
    name: FlightRawEventName.ViewedFlightList,
    funnel,
  });

  return flightList.length > 0 ? (
    <InfiniteScroll
      dataLength={flightList.length}
      next={setFetchMoreData}
      hasMore={flightList.length < numberOfFlightsRendered}
      loader={""}
    >
      <ul className={clsx(block, className)}>
        {flightList.map((flight, index) => {
          const ref = refs.current[index];
          const isSelected = expandedFlightCard === flight.sliceId;
          return (
            <li key={`flight-${flight.sliceId}`}>
              <FlightSlot.FlightCard
                flight={flight}
                isSelected={isSelected}
                onClick={() => {
                  if (expandedFlightCard === flight.sliceId) {
                    setExpandedFlightCard("");
                    setIsAllCollapsed?.(true);
                  } else {
                    setExpandedFlightCard(flight.sliceId);
                    scrollToView(ref);
                    setIsAllCollapsed?.(false);
                  }
                }}
                sliceIndex={sliceIndex}
                onFareSelect={onFareSelect}
                goToNextStep={goToNextStep}
                ref={ref}
                index={index}
              />
            </li>
          );
        })}
        {children}
      </ul>
    </InfiniteScroll>
  ) : (
    <FlightSlot.FlightListNoFlights className={cn("error")} />
  );
};
